import {
  formatAge,
  formatCadence,
  formatCompact,
  formatExact,
} from '../../types/autocoderThroughput'
import type { AutoCoderThroughputResponse } from '../../types/autocoderThroughput'

/**
 * idleModel — what the throughput page says when nothing is running.
 *
 * WHY THIS EXISTS. Every headline on this page is a TRAILING measurement:
 * tokens/min and requests/min come from a fixed 5-minute span, "pending
 * batches" is a live queue depth, and "Current run" is defined by the batches
 * still in flight. All four are correct, and all four collapse to zero the
 * instant the work finishes — so the page that was full of numbers a moment ago
 * becomes four zeroes and one sentence, and stays that way for good.
 *
 * That is not a cosmetic complaint. On 2026-08-14 a 13,000-record drain
 * completed in roughly three minutes; the operator watching it saw a blank page
 * before the run (coding was gated off) and a blank page after it (the rates had
 * decayed), with no way to tell which side of the run they were on, and
 * reasonably concluded the UI was broken. It wasn't — it just had nothing to say
 * about the recent past, only about this instant.
 *
 * So the rule these helpers encode is: A ZERO IS ONLY ALLOWED TO APPEAR
 * ALONGSIDE THE EVIDENCE THAT MAKES IT READABLE. A rate of zero is shown with
 * how long it has been zero and what the window totalled; an inactive run is
 * shown with the work that window actually completed and the lifetime tally
 * behind it.
 *
 * These are pure functions over the payload — no clock of their own, no fetching
 * — because "how long since the last call" must be measured against the SERVER's
 * generatedAt, not the browser's clock. A client whose clock is a few minutes
 * fast would otherwise render a negative age, or claim activity that has not
 * happened yet.
 */

/** Seconds between the payload's generatedAt and an earlier ISO timestamp.
 *  Returns null for a missing/unparseable stamp, and clamps at 0 so a client
 *  clock that disagrees with the server can never produce a negative age. */
export const ageSecondsFrom = (
  data: AutoCoderThroughputResponse,
  iso: string | null | undefined,
): number | null => {
  if (!iso) return null
  const then = new Date(iso).getTime()
  const now = new Date(data.generatedAt).getTime()
  if (!Number.isFinite(then) || !Number.isFinite(now)) return null
  return Math.max(0, (now - then) / 1000)
}

/** Totals for the SELECTED window, summed from the lane table. The lanes panel
 *  already holds these; recomputing them here keeps the tiles and the table
 *  incapable of disagreeing. */
export const windowTotals = (data: AutoCoderThroughputResponse): { tokens: number; requests: number } =>
  data.lanes.reduce(
    (acc, lane) => ({
      tokens: acc.tokens + lane.totalTokens,
      requests: acc.requests + lane.totalRequests,
    }),
    { tokens: 0, requests: 0 },
  )

export interface LiveTileHints {
  /** True when the trailing span saw no calls at all. */
  idle: boolean
  tokensHint: string
  requestsHint: string
}

/**
 * The hints under "Tokens / min" and "Requests / min".
 *
 * Active: names the span the rate was measured over, then the window total for
 * context. Idle: says how long it has been idle INSTEAD of naming an empty span,
 * because "last 5m" under a zero reads as a measurement failure rather than a
 * quiet queue. Never-run: says so plainly rather than inventing an age.
 */
export const liveTileHints = (data: AutoCoderThroughputResponse): LiveTileHints => {
  const totals = windowTotals(data)
  const idle = data.live.tokens === 0 && data.live.requests === 0
  const windowLabel = data.window.label.toLowerCase()

  if (!idle) {
    const span = `last ${formatCadence(data.live.spanSeconds)}`
    return {
      idle: false,
      tokensHint: `${span} · ${formatCompact(totals.tokens)} in ${windowLabel}`,
      requestsHint: `${span} · ${formatCompact(totals.requests)} in ${windowLabel}`,
    }
  }

  const age = ageSecondsFrom(data, data.live.lastActivityAt)
  if (age === null) {
    const never = 'no AutoCoder calls yet'
    return { idle: true, tokensHint: never, requestsHint: never }
  }

  const since = `idle ${formatAge(age)}`
  return {
    idle: true,
    tokensHint: `${since} · ${formatCompact(totals.tokens)} in ${windowLabel}`,
    requestsHint: `${since} · ${formatCompact(totals.requests)} in ${windowLabel}`,
  }
}

/**
 * What the lane chart says when it has no series to draw.
 *
 * "No AutoCoder LLM calls in this window" is true but unhelpful, because the
 * most common reason to see it is that the window is shorter than the gap since
 * the last run — a 60-minute view opened 70 minutes after a drain. Naming the
 * age turns a dead end into an instruction: widen the window.
 */
export const emptyLaneMessage = (data: AutoCoderThroughputResponse): string => {
  const age = ageSecondsFrom(data, data.live.lastActivityAt)
  if (age === null) return 'No AutoCoder LLM calls have been made here yet'

  const windowSeconds =
    (new Date(data.window.end).getTime() - new Date(data.window.start).getTime()) / 1000
  if (Number.isFinite(windowSeconds) && age > windowSeconds) {
    return `No calls in this window — the last one was ${formatAge(age)} ago. Try a longer window.`
  }
  return 'No AutoCoder LLM calls in this window'
}

export interface IdleRunView {
  /** Replaces the progress bar. Never the empty string. */
  headline: string
  /** What the selected window actually completed, or null when it completed
   *  nothing — in which case the window, not the queue, is the thing to change. */
  windowLine: string | null
  /** How long since the last LLM call, or null when there has never been one. */
  idleForLabel: string | null
  /** The all-time batch tally behind the window, so the panel is never empty
   *  even on a window that happens to contain nothing. */
  lifetimeLine: string | null
  /** The server's own explanation of why there is no run. */
  basis: string
}

/**
 * What "Current run" shows when `backlog.active` is false.
 *
 * It deliberately reports the WINDOW rather than trying to reconstruct "the last
 * run". A run is defined by the batches still in flight (see
 * store.AutoCoderCurrentRunStart); once they all land, the run it described no
 * longer exists as a queryable object, and inventing a boundary for it would
 * mean guessing which completed batches belonged together. The window is a
 * boundary the operator chose and can widen, which makes it honest and
 * actionable — hence "in the last 60 minutes", never a bare "last run".
 */
export const idleRunView = (data: AutoCoderThroughputResponse): IdleRunView => {
  const windowLabel = data.window.label.toLowerCase()
  const { totalRecords, totalBatches } = data.coding

  const windowLine =
    totalBatches > 0
      ? `${formatExact(totalRecords)} records coded in ${formatExact(totalBatches)} ` +
        `${totalBatches === 1 ? 'batch' : 'batches'} in ${windowLabel}`
      : null

  const age = ageSecondsFrom(data, data.live.lastActivityAt)
  const idleForLabel = age === null ? null : `last LLM call ${formatAge(age)} ago`

  const byState = new Map(data.queue.states.map((s) => [s.state, s.count]))
  const completed = byState.get('completed') ?? 0
  const failed = byState.get('failed') ?? 0
  const lifetimeLine =
    completed > 0 || failed > 0
      ? `${formatExact(completed)} batches completed all time` +
        (failed > 0 ? ` · ${formatExact(failed)} failed` : '')
      : null

  return {
    headline: totalBatches > 0 ? 'Nothing running — last completed work' : 'Nothing running',
    windowLine,
    idleForLabel,
    lifetimeLine,
    basis: data.backlog.etaBasis,
  }
}
